import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const NAV = [
  { label: "Approach", to: "/approach" },
  { label: "Markets", to: "/markets" },
  { label: "Solutions", to: "/solutions" },
  { label: "Network", to: "/network" },
  { label: "Insights", to: "/insights" },
  { label: "About", to: "/about" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 left-0 right-0 z-40 transition-all duration-500"
      style={{
        background: scrolled ? "rgba(11, 14, 19, 0.85)" : "transparent",
        borderBottom: scrolled ? "1px solid rgba(255,255,255,0.07)" : "1px solid transparent",
        backdropFilter: scrolled ? "blur(14px)" : "none",
      }}
    >
      <div className="max-w-[1500px] mx-auto px-6 lg:px-10 h-[68px] flex items-center justify-between">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-3 group">
          <span
            className="w-2 h-2 rounded-full transition-all shrink-0"
            style={{ background: "#9DB4FF", boxShadow: "0 0 0 3px rgba(157,180,255,0.18)" }}
          />
          <span className="flex flex-col leading-none">
            <span className="text-[12px] tracking-[0.45em] uppercase text-[#E8ECF1] group-hover:text-white">
              FOREALCO
            </span>
            <span className="hidden sm:block mt-1 text-[8.5px] tracking-[0.35em] uppercase text-white/40 font-mono">
              Foreign Trade · Real Access
            </span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={`text-[11px] tracking-[0.2em] uppercase transition-colors ${
                pathname === n.to ? "text-white" : "text-white/60 hover:text-white"
              }`}
            >
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Link
            to="/explore-opportunities"
            data-testid="header-cta"
            className="hidden md:inline-flex items-center text-[10px] tracking-[0.3em] uppercase px-5 py-2.5 border border-[#9DB4FF] text-[#9DB4FF] hover:bg-[#9DB4FF] hover:text-[#0E1116] transition-colors"
          >
            Explore Opportunities
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden w-10 h-10 inline-flex items-center justify-center text-white"
            onClick={() => setOpen((v) => !v)}
            aria-label="Open menu"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* mobile menu */}
      <div
        data-testid="mobile-menu"
        className={`lg:hidden overflow-hidden transition-[max-height,opacity] duration-500 ${
          open ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0"
        }`}
        style={{ background: "rgba(11,14,19,0.96)", backdropFilter: "blur(14px)" }}
      >
        <ul className="px-6 py-4 space-y-1">
          {NAV.map((n) => (
            <li key={n.to}>
              <Link
                to={n.to}
                className={`block py-3 text-[14px] tracking-[0.18em] uppercase border-b border-white/5 ${
                  pathname === n.to ? "text-white" : "text-white/65"
                }`}
              >
                {n.label}
              </Link>
            </li>
          ))}
          <li className="pt-4">
            <Link
              to="/explore-opportunities"
              data-testid="mobile-cta"
              className="block text-center text-[12px] tracking-[0.3em] uppercase px-5 py-3 bg-[#9DB4FF] text-[#0E1116]"
            >
              Explore Opportunities
            </Link>
          </li>
        </ul>
      </div>
    </header>
  );
}
